import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Percent } from "lucide-react";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

/**
 * Fee percent combobox: 0% → 50%, step 0.1%.
 * Typing "1" filters all values starting with "1" (1, 1.1, 10, 10.5, 15 …).
 */
const FEE_OPTIONS = (() => {
  const out = [];
  for (let i = 0; i <= 500; i++) {
    const v = +(i / 10).toFixed(1);
    out.push(v);
  }
  return out;
})();

export default function FeeCombobox({ value, onChange, placeholder = "0 %", testid = "fee-combo" }) {
  const [open, setOpen] = useState(false);
  const current = useMemo(() => {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(String(value).replace(",", "."));
    return isNaN(n) ? null : n;
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-white/[0.03] border-white/10 h-10 font-normal"
          data-testid={testid}
        >
          <span className="truncate flex items-center gap-2">
            {current !== null ? (
              <>
                <span className="text-zinc-100 font-mono-num">{current.toFixed(1)}</span>
                <span className="text-[10px] text-zinc-500">%</span>
              </>
            ) : (
              <span className="text-zinc-500 flex items-center gap-2">
                <Percent className="w-3.5 h-3.5" /> {placeholder}
              </span>
            )}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-zinc-500 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 bg-card border-white/10"
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        <Command className="bg-transparent" filter={(itemValue, search) => {
          // Match any value that STARTS with the typed string
          if (!search) return 1;
          return String(itemValue).startsWith(search) ? 1 : 0;
        }}>
          <CommandInput placeholder="Taper un % (ex. 1, 1.5, 25)…" data-testid={`${testid}-input`} inputMode="decimal" />
          <CommandList className="max-h-[280px] overflow-y-auto overscroll-contain" data-testid={`${testid}-list`}>
            <CommandEmpty>Aucun % correspondant.</CommandEmpty>
            <CommandGroup heading="0 % — 50 %">
              {FEE_OPTIONS.map((v) => (
                <CommandItem
                  key={v}
                  value={v.toString()}
                  onSelect={() => {
                    onChange?.(v);
                    setOpen(false);
                  }}
                  className="cursor-pointer aria-selected:bg-emerald-500/10 aria-selected:text-emerald-200"
                  data-testid={`${testid}-item-${v}`}
                >
                  <span className="font-mono-num flex-1">{v.toFixed(1)} %</span>
                  <Check className={cn("w-4 h-4", current === v ? "opacity-100 text-emerald-400" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
